import React, { ReactElement, useMemo, useState } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Send, Siren } from 'lucide-react';
import {
  ProductStatus,
  useCloseIncident,
  useCreateIncident,
  useListPmProducts,
  useOrganizationIncidents,
  useListSprints,
  useReopenIncident,
  useUpdateIncident,
} from '@data';
import { useStore } from '@data/storages';
import { IncidentTab } from '@app/utils/types';
import { useOrganizationId } from '@app/utils/hooks';
import { GenericToggler } from '@app/components/core/generic-toggler';
import { Pagination } from '@app/components/core/pagination/Pagination';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { incidentPath } from '@app/constants/url';
import { cardClass, pageHeaderRowClass, pageTitleClass } from '@app/constants/styles';
import { bySprintRecency } from '@app/utils';
import { Loader } from '@ui';
import { Button } from '@ui/components/ui/button';

import IncidentReportExportDialog from '../clients/clientDetail/IncidentReportExportDialog';
import IncidentCloseDialog from '../clientReporting/product/IncidentCloseDialog';
import IncidentEditor from '../clientReporting/product/IncidentEditor';
import IncidentFilterBar from '../clientReporting/product/IncidentFilterBar';
import IncidentResolveDialog from '../clientReporting/product/IncidentResolveDialog';
import IncidentRowActions from '../clientReporting/product/IncidentRowActions';
import { IncidentRow, OpenIncidentTable, ResolvedIncidentTable } from '../clientReporting/product/IncidentTables';
import { sprintsWithIncidents } from '../clientReporting/product/sources/incidentSprints';
import {
  IncidentDraft,
  IncidentFilters,
  IncidentView,
  agreementForLevel,
  buildClosedIncidents,
  buildOpenIncidents,
  emptyDraft,
  hasIncidentFilters,
  incidentInputFromDraft,
  incidentYearPages,
  matchesIncidentFilters,
  noIncidentFilters,
  productIncidentFromApi,
  resolutionPatch,
} from '../clientReporting/product/sources/incidents';

/** One list of what is still running, with what it is about said above it. */
const OpenCard = ({
  title,
  meaning,
  empty,
  rows,
  onOpen,
  actions,
}: {
  title: string;
  meaning: string;
  /** What an empty half says. Both halves stay on the page, so each has to say what it is empty of. */
  empty: string;
  rows: IncidentRow<IncidentView>[];
  onOpen: (row: IncidentRow<IncidentView>) => void;
  actions: (row: IncidentRow<IncidentView>) => ReactElement;
}): ReactElement => (
  <div className={cardClass}>
    <div className="mb-4">
      <p className="text-base font-semibold text-foreground">{title}</p>
      <p className="text-xs text-muted-foreground">{meaning}</p>
    </div>

    {rows.length ? (
      <OpenIncidentTable rows={rows} showProduct onOpen={onOpen} actions={actions} />
    ) : (
      <p className="py-6 text-center text-sm text-muted-foreground">{empty}</p>
    )}
  </div>
);

const IncidentsLoaded = ({ organizationId }: { organizationId: string }): ReactElement => {
  const { t } = useTranslation();
  const transitionNavigate = useViewTransitionNav();

  const [page, setPage] = useState(0);
  // One value for both halves, so they cannot disagree about what a filter means.
  const [filters, setFilters] = useState<IncidentFilters>(noIncidentFilters);
  const [draft, setDraft] = useState<IncidentDraft | null>(null);
  const [draftProductId, setDraftProductId] = useState<string>('');
  const [saveFailed, setSaveFailed] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // Kept whole: the write needs the product, and the row is what carries it on this page.
  const [resolving, setResolving] = useState<IncidentRow<IncidentView> | null>(null);
  const [closing, setClosing] = useState<IncidentRow<IncidentView> | null>(null);

  // Read back from the URL, because the toggler writes `?tab=` and a shared link has to land on that half.
  const params = new URLSearchParams(window.location.search);
  const [tab, setTab] = useState<IncidentTab>(
    params.get('tab') === IncidentTab.closed ? IncidentTab.closed : IncidentTab.open,
  );

  // Every state, including archived: an archived product can still be carrying an open incident.
  const { data: products, isLoading } = useListPmProducts(organizationId, {
    statuses: Object.values(ProductStatus) as ProductStatus[],
  });

  // The queue asks for what is still running — the same read the sidebar's count makes, so they share it.
  const { data: openData, isLoading: isLoadingIncidents } = useOrganizationIncidents(organizationId, { open: true });

  // The archive needs the whole log, asked for only once opened: the history only grows.
  const { data: closedData } = useOrganizationIncidents(organizationId, undefined, tab === IncidentTab.closed);
  const createIncident = useCreateIncident();
  const updateIncident = useUpdateIncident();
  const closeIncident = useCloseIncident();
  const reopenIncident = useReopenIncident();

  /** Which products this reader answers for. */
  const { tokenData } = useStore();

  /** Products with this reader against them. Nobody else's, an admin's included. */
  const myProductIds = useMemo(
    () =>
      new Set(
        (products?.items ?? [])
          .filter(product => (product.managerIds ?? []).includes(tokenData?.userId ?? ''))
          .map(product => product.id),
      ),
    [products?.items, tokenData?.userId],
  );

  const productList = useMemo(
    () =>
      (products?.items ?? []).map(product => ({
        id: product.id,
        name: product.name,
        status: product.status,
        supportLevel: product.supportLevel,
      })),
    [products?.items],
  );

  const rows = useMemo(() => (openData?.items ?? []).map(productIncidentFromApi), [openData?.items]);
  const closedRowsFromApi = useMemo(() => (closedData?.items ?? []).map(productIncidentFromApi), [closedData?.items]);

  const filtersActive = hasIncidentFilters(filters);

  const { data: sprintData } = useListSprints();
  const sprints = useMemo(
    () =>
      sprintsWithIncidents(
        [...(sprintData?.items ?? [])].sort(bySprintRecency),
        [...rows, ...closedRowsFromApi].map(row => row.incident.reportedAt),
      ),
    [sprintData?.items, rows, closedRowsFromApi],
  );

  const openRows: IncidentRow<IncidentView>[] = useMemo(
    () => buildOpenIncidents(productList, rows).filter(row => matchesIncidentFilters(row, filters)),
    [productList, rows, filters],
  );

  /** Split by whose product it is, because that is the first thing a reader does with this list by eye. */
  const mine = openRows.filter(row => myProductIds.has(row.productId ?? ''));
  const others = openRows.filter(row => !myProductIds.has(row.productId ?? ''));

  // The list above keeps archived products, because their old incidents still need a name in the tables.
  const loggable = useMemo(
    () =>
      productList
        .filter(product => product.status === ProductStatus.active || product.status === ProductStatus.support)
        .map(({ id, name }) => ({ id, name })),
    [productList],
  );

  // A year at a time: the unit somebody looking an incident back up thinks in.
  const closedPages = useMemo(() => {
    const shown = buildClosedIncidents(productList, closedRowsFromApi).filter(row =>
      matchesIncidentFilters(row, filters),
    );

    return incidentYearPages(shown);
  }, [productList, closedRowsFromApi, filters]);

  // Held rather than clamped: narrowing to a shorter history would leave the pager past the end.
  const yearPage = Math.min(page, Math.max(closedPages.length - 1, 0));
  const closedRows: IncidentRow<IncidentView>[] = closedPages[yearPage]?.rows ?? [];

  // Nothing until a product with an agreement is chosen: inventing targets would commit us to them.
  const agreement = useMemo(
    () => agreementForLevel(productList.find(product => product.id === draftProductId)?.supportLevel),
    [draftProductId, productList],
  );

  // Nothing chosen, including the product: a silent fallback would decide the agreement for them.
  const startNew = (): void => {
    if (!loggable.length) return;
    setDraftProductId('');
    setDraft(emptyDraft());
  };

  // A refused write leaves the log as it was, so say so rather than closing the editor.
  const onFailed = (): void => setSaveFailed(true);

  const save = (): void => {
    if (!draft || !draftProductId) return;

    createIncident.mutate(
      { productId: draftProductId, values: incidentInputFromDraft(draft) },
      { onSuccess: () => setDraft(null), onError: onFailed },
    );
  };

  /** The incident's own page: what it was, and how it went against the agreement. */
  const open = (row: IncidentRow<IncidentView>): void => {
    if (row.productId) transitionNavigate(incidentPath(organizationId, row.productId, row.incident.id));
  };

  // One write either way, so an incident cannot come out of this resolved by a click that meant to close.
  const confirmResolve = (atLocal: string, alsoClose: boolean): void => {
    if (!resolving?.productId) return;
    const target = { productId: resolving.productId, incidentId: resolving.incident.id };
    const settled = { onSuccess: () => setResolving(null), onError: onFailed };

    if (alsoClose) closeIncident.mutate({ ...target, values: resolutionPatch(atLocal) }, settled);
    else updateIncident.mutate({ ...target, values: resolutionPatch(atLocal) }, settled);
  };

  const confirmClose = (): void => {
    if (!closing?.productId) return;
    closeIncident.mutate(
      { productId: closing.productId, incidentId: closing.incident.id, values: {} },
      { onSuccess: () => setClosing(null), onError: onFailed },
    );
  };

  // No dialog: reopening decides nothing and takes nothing away.
  const reopen = (row: IncidentRow<IncidentView>): void => {
    if (!row.productId) return;
    reopenIncident.mutate({ productId: row.productId, incidentId: row.incident.id }, { onError: onFailed });
  };

  const rowActions = (row: IncidentRow<IncidentView>): ReactElement => (
    <IncidentRowActions
      incident={row.incident}
      onResolve={() => setResolving(row)}
      onClose={() => setClosing(row)}
      onReopen={() => reopen(row)}
    />
  );

  if (isLoading || isLoadingIncidents) return <Loader />;

  return (
    <div className="w-full">
      <IncidentResolveDialog
        incident={resolving?.incident ?? null}
        onResolve={confirmResolve}
        onCancel={() => setResolving(null)}
      />

      <IncidentCloseDialog
        incident={closing?.incident ?? null}
        onClose={confirmClose}
        onCancel={() => setClosing(null)}
      />

      <IncidentReportExportDialog open={exportOpen} onOpenChange={setExportOpen} organizationId={organizationId} />

      {/* The title keeps its own line on a phone, with the actions wrapping under it. */}
      <div
        className={cx(
          'mb-4.5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4',
          pageHeaderRowClass,
        )}
      >
        <h1 className={cx('m-0', pageTitleClass)}>{t('sidebar.incidents')}</h1>
        <div className="flex flex-wrap items-center gap-2 md:gap-4">
          <Button variant="outline" className="bg-card" disabled={!loggable.length} onClick={startNew}>
            <Siren className="size-4" />
            {t('incidents.logIncident')}
          </Button>
          {/* No client in context here, so the export asks which one first — a batch belongs to exactly
              one client, which is what keeps one client's report out of another's mail. */}
          <Button variant="outline" className="bg-card" onClick={() => setExportOpen(true)}>
            <Send className="size-4" />
            {t('incidentReports.exportAction')}
          </Button>
          <GenericToggler tabs={[IncidentTab.open, IncidentTab.closed]} activeTab={tab} setActiveTab={setTab} />
        </div>
      </div>

      {!loggable.length ? (
        <p className="rounded-xl border border-dashed border-border px-3 py-10 text-center text-sm text-muted-foreground">
          {t('incidents.noProducts')}
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {/* One bar over both halves, so following a product from the queue into the archive picks it once. */}
          <IncidentFilterBar products={loggable} sprints={sprints} value={filters} onChange={setFilters} />

          {tab === IncidentTab.open && (
            <>
              {/* Narrowed to nothing and empty are different answers; only the first is fixed by clearing. */}
              <OpenCard
                title={t('incidents.yoursSection')}
                meaning={t('incidents.yoursMeaning')}
                empty={t(filtersActive ? 'incidents.emptyFilteredOpen' : 'incidents.nothingYours')}
                rows={mine}
                onOpen={open}
                actions={rowActions}
              />
              <OpenCard
                title={t('incidents.othersSection')}
                meaning={t('incidents.othersMeaning')}
                empty={t(filtersActive ? 'incidents.emptyFilteredOpen' : 'incidents.nothingOthers')}
                rows={others}
                onOpen={open}
                actions={rowActions}
              />
            </>
          )}

          {tab === IncidentTab.closed && (
            <div className={cardClass}>
              <div className="mb-4">
                <p className="text-base font-semibold text-foreground">{t('incidents.closedHeading')}</p>
                <p className="text-xs text-muted-foreground">{t('incidents.closedMeaning')}</p>
              </div>

              {closedRows.length ? (
                <ResolvedIncidentTable
                  rows={closedRows}
                  showProduct
                  outcomes={false}
                  onOpen={open}
                  actions={rowActions}
                />
              ) : (
                // Narrowed to nothing and empty are different answers, and say so differently.
                <p className="py-6 text-center text-sm text-muted-foreground">
                  {t(filtersActive ? 'incidents.emptyFiltered' : 'incidents.emptyClosed')}
                </p>
              )}
            </div>
          )}

          {/* Above the pager: its `-mb-10` lies over whatever follows it. */}
          {saveFailed && <p className="text-[11px] text-destructive">{t('incidents.saveFailed')}</p>}

          {/* One page per year, reversed so years read left to right. Last on the page: `-mb-10` overlaps. */}
          {tab === IncidentTab.closed && (
            <Pagination
              className="mt-4"
              page={yearPage}
              pageCount={closedPages.length}
              onChange={setPage}
              labels={closedPages.map(entry => String(entry.year))}
              reversed
            />
          )}
        </div>
      )}

      <IncidentEditor
        draft={draft}
        agreement={agreement}
        isNew
        products={loggable}
        productId={draftProductId}
        onProductChange={setDraftProductId}
        onChange={setDraft}
        onSave={save}
        onCancel={() => setDraft(null)}
      />
    </div>
  );
};

const Incidents = (): ReactElement => {
  const organizationId = useOrganizationId();

  return organizationId ? <IncidentsLoaded organizationId={organizationId} /> : <Loader />;
};

export default Incidents;
